// @oasis-permission-inherits: /jerboa-client-error (jerboa:view)
import { Link, createFileRoute } from "@tanstack/react-router";

import { Skeleton } from "~/components/ui/skeleton";
import { getJerboaClientErrorQuery } from "~/modules/jerboa-client-error/queries";
import type { JerboaClientErrorSummary } from "~/modules/jerboa-client-error/schemas";
import { SeverityBadge } from "~/modules/jerboa-client-error/severity-badge";
import { formatTimestamp } from "~/modules/jerboa-client-preview/formatters";

export const Route = createFileRoute("/_dash/jerboa-client-error/$id")({
  component: RouteComponent,
});

function RouteComponent() {
  const { id } = Route.useParams();
  const { data, isLoading, error } = getJerboaClientErrorQuery.useQuery({
    pathParams: { id },
  });
  const row = data?.result;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <Link to="/jerboa-client-error" className="text-sm text-blue-600 hover:underline">
            {"<"} Client Errors
          </Link>
          <h1 className="mt-2 text-xl font-bold">Client Error</h1>
        </div>
        {row && <SeverityBadge severity={row.errorInfo.severity} />}
      </div>

      {isLoading && (
        <div className="space-y-3">
          <Skeleton className="h-24 w-full rounded-md" />
          <Skeleton className="h-48 w-full rounded-md" />
        </div>
      )}

      {error && (
        <div className="rounded-md border border-destructive bg-destructive/10 p-4 text-sm text-destructive">
          <p className="font-medium">Failed to load client error</p>
          <pre className="mt-2 whitespace-pre-wrap text-xs">{error.message}</pre>
        </div>
      )}

      {row && (
        <>
          <div className="grid gap-3 md:grid-cols-3">
            <InfoCard title="Error" rows={errorRows(row)} />
            <InfoCard title="Browser" rows={browserRows(row)} />
            <InfoCard title="Correlation" rows={correlationRows(row)} />
          </div>

          <section className="rounded-md border p-4">
            <h2 className="mb-2 text-sm font-semibold">Message</h2>
            <p className="whitespace-pre-wrap text-sm">{row.errorInfo.message || "-"}</p>
          </section>

          {row.errorInfo.stack && (
            <section className="rounded-md border p-4">
              <h2 className="mb-2 text-sm font-semibold">Stack</h2>
              <pre className="overflow-auto rounded bg-muted p-3 text-xs">
                {row.errorInfo.stack}
              </pre>
            </section>
          )}

          <section className="rounded-md border p-4">
            <h2 className="mb-2 text-sm font-semibold">Raw Payload</h2>
            <pre className="overflow-auto rounded bg-muted p-3 text-xs">
              {JSON.stringify(row, null, 2)}
            </pre>
          </section>
        </>
      )}
    </div>
  );
}

type InfoRow = [string, string | null | undefined];

function InfoCard({ title, rows }: { title: string; rows: InfoRow[] }) {
  return (
    <section className="rounded-md border p-4">
      <h2 className="mb-3 text-sm font-semibold">{title}</h2>
      <dl className="space-y-2 text-sm">
        {rows.map(([label, value]) => (
          <div key={label} className="grid grid-cols-[110px_1fr] gap-3">
            <dt className="text-muted-foreground">{label}</dt>
            <dd className="min-w-0 truncate font-mono text-xs">{value || "-"}</dd>
          </div>
        ))}
      </dl>
    </section>
  );
}

function errorRows(row: JerboaClientErrorSummary): InfoRow[] {
  return [
    ["Severity", row.errorInfo.severity],
    ["Code", row.errorInfo.code],
    ["Subsystem", row.errorInfo.subsystem],
    ["Context", row.errorInfo.context],
    ["Fingerprint", row.fingerprint],
    ["Occurred At", new Date(row.occurredAtMs).toISOString()],
    ["Created", formatTimestamp(row.createdAt)],
    ...(row.noise ? ([["Noise", row.noiseReason ?? "noise"]] as InfoRow[]) : []),
  ];
}

function browserRows(row: JerboaClientErrorSummary): InfoRow[] {
  return [
    ["Browser", row.browserInfo?.browserName],
    ["OS", row.browserInfo?.os],
    ["OS Version", row.browserInfo?.osVersion],
    ["GPU", row.browserInfo?.gpuRenderer],
    ["Mirage", row.appVersion],
    ["Jerboa", row.jerboaVersion],
    ["User Agent", row.browserInfo?.userAgent],
  ];
}

function correlationRows(row: JerboaClientErrorSummary): InfoRow[] {
  return [
    ["Session", row.sessionId],
    ["Job", row.jobId],
    ["Video", row.videoId],
    ["Project", row.projectId],
    ["Captions", row.captionsProjectId],
    ["Edit Type", row.editType],
  ];
}
